import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	Output,
	SimpleChanges,
	ViewEncapsulation,
} from '@angular/core';
import {
	AbstractControl,
	FormControl,
	FormGroup,
	Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { CroppedAvatar, ZkNotificationService } from '@zenklub/ng-ui';
import { ZkIOption } from '@zenklub/ng-ui/component/input/dropdown/dropdown.interface';
import { ZkTInputStatus } from '@zenklub/ng-ui/component/input/input-status/input-status.interface';
import {
	INTERACTION_TYPE,
	ITEM_TYPE,
	SCOPE_TYPE,
	SUBSCOPE_TYPE,
} from 'app/interfaces/actiontracker.interface';
import { IRegion } from 'app/interfaces/location.interface';
import {
	IProfessionalProfile,
	IProfessionalProfileUpdate,
} from 'app/interfaces/professionalprofile.interface';
import { IEthnicity } from 'app/interfaces/search.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { LocationService } from 'app/services/location.service';
import { SearchService } from 'app/services/search.service';
import { UserService } from 'app/services/user.service';

const messages = require('./messages.json');

@Component({
	selector: 'app-my-account-personal',
	templateUrl: './personal.component.html',
	styleUrls: ['./personal.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class MyAccountPersonalComponent implements OnChanges {
	@Input() user: IProfessionalProfile;
	@Output() userChanges = new EventEmitter<IProfessionalProfileUpdate>();
	@Output() unsavedChanges = new EventEmitter<boolean>();

	form: FormGroup = new FormGroup({
		imageUrl: new FormControl('', [
			Validators.required,
			() => {
				const [error, value] =
					Object.entries(this.imageUrlError || {})[0] || [];
				const response = {};
				if (error) response[error] = value;
				return error ? response : null;
			},
		]),
		name: new FormControl('', [
			Validators.required,
			Validators.minLength(6),
			Validators.maxLength(50),
			({ value }: AbstractControl) =>
				!value || value.trim().includes(' ') ? null : { shortName: true },
		]),
		phone: new FormControl('', Validators.required),
		birthDate: new FormControl('', [
			Validators.required,
			({ value }) => {
				if (!value) return null;
				const today = new Date().getTime();
				const birthDate = new Date(value).getTime();
				const age = Math.ceil(
					(today - birthDate) / (1000 * 60 * 60 * 24 * 365)
				);
				return age <= 18 || age >= 100 ? { range: age } : null;
			},
		]),
		gender: new FormControl('', Validators.required),
		ethnicity: new FormControl('', Validators.required),
		location: new FormControl('', Validators.required),
		isLgbt: new FormControl(false),
	});

	gender: ZkIOption[] = [];
	resEthnicity: IEthnicity[] = [];
	ethnicity: ZkIOption[] = [];
	resLocation: IRegion[] = [];
	location: ZkIOption[] = [];

	imageUrlError = null;
	isLoading = false;
	showChangePasswordDialog = false;
	saveNewPassword = false;
	readonly lgbtTag = 'professional_lgbt';

	constructor(
		public locationService: LocationService,
		public notificationService: ZkNotificationService,
		public searchService: SearchService,
		public translateService: TranslateService,
		public userService: UserService,
		public actionTrackerService: ActionTrackerService
	) {
		this.getEthnicityList();
		this.getLocations();
		this.listenFormChanges();
	}

	ngOnChanges(changes: SimpleChanges): void {
		const user: IProfessionalProfile = changes.user?.currentValue;
		if (user) {
			this.updateFormValues(user);
			this.setGenders();
		}
		this.unsavedChanges.emit(this.form.dirty);
	}

	updateFormValues(user: IProfessionalProfile): void {
		this.form.setValue({
			imageUrl: user.imageUrl || '',
			name: user.displayName || '',
			phone: user.phoneNumber || '',
			birthDate: (user.birthdate && new Date(user.birthdate)) || '',
			gender: user.gender || '',
			ethnicity: user.ethnicity?.name || '',
			location: user.location?.region || '',
			isLgbt: user.tags.some((tag) => tag.id === this.lgbtTag),
		});
		this.form.markAsPristine();
	}

	setGenders(): void {
		const translatePath = 'MY_ACCOUNT.PERSONAL.GENDER_OPTIONS';

		this.translateService
			.get([translatePath])
			.subscribe(({ [translatePath]: optionsObj }) => {
				for (const property in optionsObj) {
					this.gender.push(optionsObj[property]);
				}
			});
	}

	getEthnicityList(): void {
		this.searchService.getEthnicityList().subscribe((res) => {
			this.resEthnicity = res;
			this.ethnicity = res.map(({ name }) => ({
				label: name,
				value: name,
			}));
		});
	}

	getLocations(): void {
		this.locationService.getCountryRegions(1).subscribe((res) => {
			this.resLocation = res;
			this.location = res.map(({ name }) => ({ label: name, value: name }));
		});
	}

	handleImageUrlError(error: string): void {
		const control = this.form.get('imageUrl');
		const { smallDimensions } = messages['imageUrl'];
		switch (error) {
			case 'SMALL_DIMENSIONS':
				control.setErrors({ smallDimensions });
				this.imageUrlError = { smallDimensions };
				break;
			default:
				control.setErrors({});
				this.imageUrlError = null;
				break;
		}
		control.markAsDirty();
		control.markAsTouched();
	}

	base64MimeType(encoded) {
		if (typeof encoded === 'string' && encoded) {
			const mime = encoded.substring(
				'data:'.length,
				encoded.indexOf(';base64')
			);
			if (mime) {
				return mime;
			}
		}
		return null;
	}

	imageUrlChange(croppedAvatar: CroppedAvatar): void {
		const fileType = this.base64MimeType(croppedAvatar.base64) || 'image/jpeg';
		const file = new File([croppedAvatar.blob], 'avatar', { type: fileType });
		this.form.controls.imageUrl.setErrors({ uploadingImage: true });
		this.userService.updatePhoto(file).subscribe(
			(imageUrl) => {
				this.imageUrlError = null;
				this.form.controls.imageUrl.setErrors(null);
				this.form.controls.imageUrl.setValue(imageUrl);
				this.form.markAsDirty();
			},
			(e) => {
				const error = { errorUpload: messages.imageUrl.errorUpload };
				this.form.controls.imageUrl.setErrors(error);
				this.imageUrlError = error;
				this.notificationService.showError(
					e?.error?.messageI18n ||
						this.translateService.instant('MESSAGES.SAVEDERROR')
				);
			}
		);
	}

	getBirthDateRange(): string {
		return `1900:${new Date().getFullYear()}`;
	}

	getStatus(formControlName: string): ZkTInputStatus {
		const { errors, dirty, touched } = this.form.get(formControlName);
		return (dirty || touched) && errors && Object.keys(errors)
			? 'error'
			: 'default';
	}

	getErrorMessage(formControlName: string): string {
		const { errors, dirty, touched } = this.form.get(formControlName);
		if (!(dirty || touched) || !errors) return '';

		const e = Object.entries(errors)[0];
		let error = e[0];
		const value = e[1];

		if (formControlName === 'birthDate') {
			if (error === 'required' && value) {
				error = 'required';
			} else if (value <= 18) {
				error = 'belowTheRange';
			} else if (value >= 100) {
				error = 'aboveTheRange';
			}
		}

		return messages[formControlName][error] || '';
	}

	save(event): void {
		event.preventDefault();
		event.stopPropagation();

		this.form.markAllAsTouched();
		if (!this.form.valid || !this.form.dirty) return;

		this.isLoading = true;
		const data = this.handleData();
		this.emitUserChanges(data);
		this.updateProfileData(data);
	}

	handleData(): IProfessionalProfileUpdate {
		const values = this.form.value;
		const data = {
			...this.user,
			imageUrl: values.imageUrl,
			displayName: values.name,
			phoneNumber: values.phone,
			birthdate: values.birthDate,
			gender: values.gender,
			ethnicity: this.resEthnicity
				.map(({ name, id }) => {
					if (name === values.ethnicity) return id;
				})
				.filter((e) => e)[0],
			location: this.resLocation
				.map(({ country, name }) => {
					if (name === values.location)
						return { country: country.code, region: name };
				})
				.filter((e) => e)[0],
			// We send only the spokenLanguages ids
			spokenLanguages: this.user.spokenLanguages.map((lang) => lang.id),
			tags: this.user.tags.map((tag) => tag.id),
		};

		const hasLgbtTag = this.user.tags.some((tag) => tag.id === this.lgbtTag);

		if (values.isLgbt && !hasLgbtTag) {
			data.tags.push(this.lgbtTag);
		}

		if (!values.isLgbt && hasLgbtTag) {
			data.tags = data.tags.filter((tag) => tag !== this.lgbtTag);
		}

		// Delete not necessary properties.
		delete data['active'];
		delete data['createdAt'];
		delete data['fullName'];
		delete data['id'];
		delete data['isOnline'];
		delete data['isVisible'];
		delete data['types'];
		delete data['updatedAt'];
		delete data['_t'];

		// E-Mail is ready-only.
		data.email = this.user.email;

		return data;
	}

	updateProfileData(data: IProfessionalProfileUpdate): void {
		if (data.tags?.some((tag) => tag === this.lgbtTag)) {
			this.actionTrackerService.eventTracker(
				SCOPE_TYPE.Account,
				SUBSCOPE_TYPE.Profile,
				ITEM_TYPE.IsLgbt,
				INTERACTION_TYPE.Check
			);
		}
		this.userService.updateProfileData(data, '/personal').subscribe(
			() => {
				this.notificationService.showSuccess(
					this.translateService.instant('MESSAGES.SAVEDSUCCESSFULLY')
				);

				this.isLoading = false;
				this.form.markAsPristine();
				this.unsavedChanges.emit(this.form.dirty);
			},
			({ error }) => {
				this.notificationService.showError(
					error?.messageI18n,
					this.translateService.instant('MESSAGES.SAVEDERROR')
				);

				this.isLoading = false;
			}
		);
	}

	emitUserChanges(data: IProfessionalProfileUpdate): void {
		this.userChanges.emit(data);
	}

	passwordChanged(): void {
		this.showChangePasswordDialog = false;
		this.notificationService.showSuccess(
			this.translateService.instant('MESSAGES.SAVEDSUCCESSFULLY')
		);
	}

	listenFormChanges(): void {
		this.form.valueChanges.subscribe(() => {
			this.unsavedChanges.emit(this.form.dirty);
		});
	}
}
